import React, { useMemo } from "react";
import Storyline from "./Storyline/Storyline";
import Home from "./Home/Home";
import * as _ from "lodash";
import {
    Routes,
    Route,
    Navigate,
    useParams,
    useLocation,
    Outlet,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { loadStoryline as _loadStoryline, loadSnapshot as _loadSnapshot } from "../store/storyline/actions";
import { setMenuIsExpanded as _setMenuIsExpanded } from "../store/app/actions";
import { RootState, useThunkDispatch } from "../store";
import useQueryParams from "../shared/hooks/useQueryParams";
import clsx from "clsx";
import Sidenav from "../shared/components/Sidenav";
import SidenavMiniMap from "./SidenavMiniMap/SidenavMiniMap";
import MySnapshots from "./pages/MySnapshots";
import NotFound from "../shared/pages/NotFound";
import * as BaseComponents from "../shared/components/CanvasComponents";
import * as StorylineComponents from "./components";
import { pluginComponents } from "../shared/services/pluginService";
import { JsxParserComponentsContext } from "../shared/providers/JsxParserComponentsProvider";
import { openWsConnection } from "../store/vscode/actions";
import { AnchorWrapper } from "../shared/components/AnchorWrapper";
import { createSelector } from "@reduxjs/toolkit";

const activeItemContent = <SidenavMiniMap />;

function NavigateToHomeOrDefaultPage(props: { defaultPageUrl?: string }) {
    const { defaultPageUrl } = props;

    return (
        defaultPageUrl ?
            <Navigate to={defaultPageUrl} /> :
            <Home />
    );
}

export interface SnapshotReference {
    owner: string;
    snapshotId: string;
}

export function RenderStoryline(props: { loader: (id: string, snapshotReference?: SnapshotReference) => any }) {
    const { loader } = props;
    const { id } = useParams();
    const activeBranch = useSelector((store: RootState) => store.app.activeBranch);

    // Read the snapshot params from the raw query string - useQueryParams' type coercion
    // (dates, numbers, obfuscated-parameter decoding) must not touch these identifiers...
    const search = useLocation().search;
    const { snapshotId, snapshotOwner } = React.useMemo(() => {
        const searchParams = new URLSearchParams(search);
        return {
            snapshotId: searchParams.get("_snapshot"),
            snapshotOwner: searchParams.get("_snapshotOwner")
        };
    }, [search]);

    React.useEffect(() => {
        loader(id, snapshotId && snapshotOwner ? { owner: snapshotOwner, snapshotId } : undefined);
    }, [id, activeBranch, snapshotId, snapshotOwner]);

    return (
        <Storyline />
    );
}

// Module-scoped so the selector's memo cache survives across renders...
const visibilityClassesSelector = createSelector(
    [
        (state: RootState) => state.storyline.parameterValues.get("isExport"),
        (state: RootState) => state.storyline.parameterValues.get("hideTitleBar"),
        (state: RootState) => state.storyline.parameterValues.get("hideMinimap"),
        (state: RootState) => state.storyline.parameterValues.get("hideCommentary")
    ],
    (isExport, hideTitleBar, hideMinimap, hideCommentary) => [
        isExport === "1" ? "is-export" : undefined,
        hideTitleBar === "1" ? "hide-titlebar" : undefined,
        hideMinimap === "1" ? "hide-minimap" : undefined,
        hideCommentary === "1" ? "hide-commentary" : undefined
    ]
);

export function useViewerState(loadStoryline: (id: string, parameterValues: Map<string, any>) => void, setMenuIsExpanded: (value: boolean) => { type: string; value: boolean; }, loadSnapshot?: (owner: string, snapshotId: string) => void) {
    const visibilityClasses = useSelector(visibilityClassesSelector);

    const queryParams = useQueryParams();

    const ensureCorrectStorylineIsLoaded = (loader: typeof loadStoryline) => (id: string, snapshotReference?: SnapshotReference) => {
        // A snapshot link bypasses parameter assembly entirely - the captured state is authoritative...
        if (snapshotReference && loadSnapshot) {
            loadSnapshot(snapshotReference.owner, snapshotReference.snapshotId);
            return;
        }

        // If nav-filters-id parameter is passed in, fetch the parameter values from session storage and include those in the full parameter set...
        const navFiltersId = queryParams.get("nav-filters-id");
        const navFiltersParams = navFiltersId ?
            JSON.parse(sessionStorage.getItem(`nav-filters-${navFiltersId}`) ?? "{}") :
            {};

        const allParameters = new Map<string, any>([
            ...Object.entries(navFiltersParams),
            ...queryParams.entries()
        ]);

        // Snapshot identifiers are routing concerns, never canvas parameters...
        allParameters.delete("_snapshot");
        allParameters.delete("_snapshotOwner");

        if (allParameters.has("menu-expanded")) {
            const expandedParamValue = allParameters.get("menu-expanded");
            setMenuIsExpanded(expandedParamValue === 1 || expandedParamValue === "true");
        }

        loader(id, allParameters);
    };
    return { visibilityClasses, ensureCorrectStorylineIsLoaded };
}

interface ViewerProps {
    menuItems: RootState["app"]["menuItems"],
}

const Viewer: React.FC<ViewerProps> = ({ menuItems }) => {
    const storylineId = useSelector((state: RootState) => state.storyline.id);
    const defaultPageUrl = useSelector((state: RootState) => state.app?.settings?.defaultPageUrl);
    const isDeveloper = useSelector((state: RootState) => state.app.user.isDeveloper);

    const dispatch = useThunkDispatch();
    const loadStoryline = React.useCallback((id: string, parameterValues: Map<string, any>) => {
        return dispatch(_loadStoryline(id, parameterValues));
    }, []);
    const loadSnapshot = React.useCallback((owner: string, snapshotId: string) => {
        return dispatch(_loadSnapshot(owner, snapshotId));
    }, []);
    const setMenuIsExpanded = React.useCallback((value: boolean) => {
        return dispatch(_setMenuIsExpanded(value));
    }, []);

    const components = useMemo(() => {
        const result = { ...BaseComponents, ...StorylineComponents, ...pluginComponents, a: AnchorWrapper } as Record<string, any>;
        window.__QERENT_COMPONENTS__ = result;
        return result;
    }, [pluginComponents]);
    const { visibilityClasses, ensureCorrectStorylineIsLoaded } = useViewerState(loadStoryline, setMenuIsExpanded, loadSnapshot);

    React.useEffect(() => {
        // Immediately try to open the VS Code WebSocket connection if the user is a developer...
        if (isDeveloper && window.location.hostname !== "documentation.dev.qerent.ai") {
            dispatch(openWsConnection(components));
        }
    }, []);

    return (
        <JsxParserComponentsContext.Provider value={components}>
            <Routes>
                <Route element={
                    <div id={`storyline-${storylineId}`} className="app-layout storyline-page">
                        <Sidenav menuItems={menuItems} activeItemContent={activeItemContent} className={clsx(visibilityClasses)} />
                        <div className={clsx("content", visibilityClasses)}>
                            <Outlet />
                        </div>
                    </div>
                }>
                    <Route index element={<NavigateToHomeOrDefaultPage defaultPageUrl={defaultPageUrl} />} />
                    <Route path="storylines/:id" element={<RenderStoryline loader={ensureCorrectStorylineIsLoaded(loadStoryline)} />} />
                    <Route path="snapshots" element={<MySnapshots />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </JsxParserComponentsContext.Provider>
    );
}

export default React.memo(Viewer);
